import * as i0 from '@angular/core';
import { Component, ChangeDetectionStrategy, HostBinding, HostListener, NgModule, Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { trigger, transition, animate, keyframes, style } from '@angular/animations';
import * as i1 from '@angular/common';
import { CommonModule } from '@angular/common';
import { __decorate } from 'tslib';
import { ComponentPortal } from '@angular/cdk/portal';
import { untilDestroyed, UntilDestroy } from '@ngneat/until-destroy';
import * as i1$1 from '@angular/cdk/overlay';
import { OverlayModule } from '@angular/cdk/overlay';

var GpuToastType;
(function (GpuToastType) {
    GpuToastType["Error"] = "error";
    GpuToastType["Success"] = "success";
    GpuToastType["Info"] = "info";
    GpuToastType["Warning"] = "warning";
    GpuToastType["General"] = "general";
})(GpuToastType || (GpuToastType = {}));

const toastFlyInOutAnimation = trigger('flyInOut', [
    transition('void => *', animate('300ms ease-in', keyframes([
        style({
            transform: 'translate3d(0, 100%, 0)',
            opacity: 0,
        }),
        style({
            transform: 'none',
            opacity: 1,
        }),
    ]))),
    transition('* => void', animate('300ms ease-in-out', keyframes([
        style({
            opacity: 1,
        }),
        style({
            transform: 'translate3d(0, 100%, 0)',
            opacity: 0,
        }),
    ]))),
]);

class GpuToastComponent {
    constructor() {
        this.toastClass = null;
        this.onClose = new Subject();
    }
    get slideIn() {
        return true;
    }
    tapToast() {
        if (this.options.tapToDismiss) {
            this.dismiss();
        }
    }
    dismiss() {
        if (this.options.buttonClicked$) {
            this.options.buttonClicked$.next();
        }
        this.onClose.next();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: GpuToastComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.1.3", type: GpuToastComponent, selector: "gpu-toast", host: { listeners: { "click": "tapToast()" }, properties: { "class": "this.toastClass", "@flyInOut": "this.slideIn" } }, ngImport: i0, template: "<div class=\"d-flex gp-16 gp-sm-24\" [ngClass]=\"{ 'flex-wrap flex-sm-nowrap gap': buttonText }\">\n  <p\n    *ngIf=\"message && options.enableHtml\"\n    role=\"alertdialog\"\n    aria-live=\"polite\"\n    class=\"p3-regular gm-0 flex-grow-1\"\n    [class]=\"options.messageClass\"\n    [innerHTML]=\"message\"\n  ></p>\n  <p\n    *ngIf=\"message && !options.enableHtml\"\n    role=\"alertdialog\"\n    aria-live=\"polite\"\n    class=\"p3-regular gm-0\"\n    [class]=\"options.messageClass\"\n    [attr.aria-label]=\"message\"\n  >\n    {{ message }}\n  </p>\n\n  <ng-container *ngIf=\"options?.closeButton\">\n    <div *ngIf=\"buttonText\" class=\"d-flex flex-grow-1 justify-content-end align-items-end gpl-16 gpl-sm-24\">\n      <button class=\"gbtn-sm gbtn-sq toast-button b3-semibold\" [attr.data-track]=\"dataTrack\" (click)=\"dismiss()\">\n        {{ buttonText }}\n      </button>\n    </div>\n    <button *ngIf=\"!buttonText\" class=\"icon-btn gml-8 gml-sm-16\" (click)=\"dismiss()\">\n      <i class=\"gpicon-close\"></i>\n    </button>\n  </ng-container>\n</div>\n", styles: [":host{background-image:unset;background-color:#fff;position:relative;overflow:hidden;margin:0 auto;width:max-content;border-radius:.75rem;color:#616680;pointer-events:all;cursor:default;box-shadow:0 10px 20px 5px #4e5f7c1a;border:1px solid #e5e5ea}@media (min-width: 768px){:host{max-width:672px}}@media (min-width: 992px){:host{max-width:852px}}@media (max-width: 767.98px){:host .gap{gap:12px}}@media (max-width: 575.98px){:host{margin:0 1rem;width:100%}}:host button{min-width:max-content}:host button.toast-button{background-color:#eceffe;color:#6a81f6;border:none}:host button.icon-btn{padding:0;color:inherit;border:unset;background-color:unset;outline:unset;transition:opacity .2s}:host button.icon-btn:hover{opacity:.8}:host button.icon-btn:active{opacity:.7}:host.toast-success{color:#00b9bb;box-shadow:0 10px 20px 5px #7adadc33;border:1px solid #7adadc}:host.toast-success button.toast-button{background-color:#d9f5f5;color:#00b9bb;border:none}:host.toast-error{color:#ff397f;box-shadow:0 10px 20px 5px #ff9bbe2e;border:1px solid #ff9bbe}:host.toast-error button.toast-button{background-color:#ffe6ee;color:#ff397f;border:none}:host.toast-warning{color:#ff9f00;box-shadow:0 10px 20px 5px #ffcf8033;border:1px solid #ffcf80}:host.toast-warning button.toast-button{background-color:#fff3df;color:#ff9f00;border:none}:host.toast-info{color:#6a81f6;box-shadow:0 10px 20px 5px #b4c0fb33;border:1px solid #b4c0fb}:host.toast-info button.toast-button{background-color:#eceffe;color:#6a81f6;border:none}\n"], dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }], animations: [toastFlyInOutAnimation], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: GpuToastComponent, decorators: [{
            type: Component,
            args: [{ selector: 'gpu-toast', animations: [toastFlyInOutAnimation], preserveWhitespaces: false, changeDetection: ChangeDetectionStrategy.OnPush, template: "<div class=\"d-flex gp-16 gp-sm-24\" [ngClass]=\"{ 'flex-wrap flex-sm-nowrap gap': buttonText }\">\n  <p\n    *ngIf=\"message && options.enableHtml\"\n    role=\"alertdialog\"\n    aria-live=\"polite\"\n    class=\"p3-regular gm-0 flex-grow-1\"\n    [class]=\"options.messageClass\"\n    [innerHTML]=\"message\"\n  ></p>\n  <p\n    *ngIf=\"message && !options.enableHtml\"\n    role=\"alertdialog\"\n    aria-live=\"polite\"\n    class=\"p3-regular gm-0\"\n    [class]=\"options.messageClass\"\n    [attr.aria-label]=\"message\"\n  >\n    {{ message }}\n  </p>\n\n  <ng-container *ngIf=\"options?.closeButton\">\n    <div *ngIf=\"buttonText\" class=\"d-flex flex-grow-1 justify-content-end align-items-end gpl-16 gpl-sm-24\">\n      <button class=\"gbtn-sm gbtn-sq toast-button b3-semibold\" [attr.data-track]=\"dataTrack\" (click)=\"dismiss()\">\n        {{ buttonText }}\n      </button>\n    </div>\n    <button *ngIf=\"!buttonText\" class=\"icon-btn gml-8 gml-sm-16\" (click)=\"dismiss()\">\n      <i class=\"gpicon-close\"></i>\n    </button>\n  </ng-container>\n</div>\n", styles: [":host{background-image:unset;background-color:#fff;position:relative;overflow:hidden;margin:0 auto;width:max-content;border-radius:.75rem;color:#616680;pointer-events:all;cursor:default;box-shadow:0 10px 20px 5px #4e5f7c1a;border:1px solid #e5e5ea}@media (min-width: 768px){:host{max-width:672px}}@media (min-width: 992px){:host{max-width:852px}}@media (max-width: 767.98px){:host .gap{gap:12px}}@media (max-width: 575.98px){:host{margin:0 1rem;width:100%}}:host button{min-width:max-content}:host button.toast-button{background-color:#eceffe;color:#6a81f6;border:none}:host button.icon-btn{padding:0;color:inherit;border:unset;background-color:unset;outline:unset;transition:opacity .2s}:host button.icon-btn:hover{opacity:.8}:host button.icon-btn:active{opacity:.7}:host.toast-success{color:#00b9bb;box-shadow:0 10px 20px 5px #7adadc33;border:1px solid #7adadc}:host.toast-success button.toast-button{background-color:#d9f5f5;color:#00b9bb;border:none}:host.toast-error{color:#ff397f;box-shadow:0 10px 20px 5px #ff9bbe2e;border:1px solid #ff9bbe}:host.toast-error button.toast-button{background-color:#ffe6ee;color:#ff397f;border:none}:host.toast-warning{color:#ff9f00;box-shadow:0 10px 20px 5px #ffcf8033;border:1px solid #ffcf80}:host.toast-warning button.toast-button{background-color:#fff3df;color:#ff9f00;border:none}:host.toast-info{color:#6a81f6;box-shadow:0 10px 20px 5px #b4c0fb33;border:1px solid #b4c0fb}:host.toast-info button.toast-button{background-color:#eceffe;color:#6a81f6;border:none}\n"] }]
        }], propDecorators: { toastClass: [{
                type: HostBinding,
                args: ['class']
            }], slideIn: [{
                type: HostBinding,
                args: ['@flyInOut']
            }], tapToast: [{
                type: HostListener,
                args: ['click']
            }] } });

const defaultToastConfig = {
    closeButton: true,
    disableTimeout: false,
    timeout: 5000,
    tapToDismiss: false,
    toastClass: '',
    messageClass: '',
    enableHtml: false,
};

class GpuToastModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: GpuToastModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.1.3", ngImport: i0, type: GpuToastModule, declarations: [GpuToastComponent], imports: [CommonModule, OverlayModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: GpuToastModule, imports: [CommonModule, OverlayModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: GpuToastModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [GpuToastComponent],
                    imports: [CommonModule, OverlayModule],
                }]
        }] });

let GpuToastService = class GpuToastService {
    constructor(overlay) {
        this.overlay = overlay;
        this.overlayConfig = this.overlay.position().global().centerHorizontally().bottom('1.5rem');
        this.dismissSubscription = null;
        this.createOverlay();
    }
    show(content, type, override, dataTrack) {
        if (this.overlayRef?.hasAttached()) {
            this.dismiss(true);
            this.createOverlay();
        }
        const toastPortal = new ComponentPortal(GpuToastComponent);
        const toastRef = this.overlayRef.attach(toastPortal);
        const options = this.applyConfig(override);
        toastRef.instance.message = content.text;
        toastRef.instance.buttonText = content.buttonText;
        toastRef.instance.options = options;
        toastRef.instance.toastClass = `toast-${type}`;
        toastRef.instance.dataTrack = dataTrack ?? undefined;
        this.dismissSubscription = toastRef.instance.onClose.pipe(untilDestroyed(this)).subscribe(() => this.dismiss());
        if (options.timeout && !options.disableTimeout) {
            this.setAutoDismiss(options.timeout);
        }
        return toastRef;
    }
    dismiss(dispose = false) {
        if (this.overlayRef && this.overlayRef.hasAttached()) {
            this.overlayRef.detach();
            if (dispose) {
                this.overlayRef.dispose();
            }
        }
        if (this.dismissSubscription) {
            this.dismissSubscription?.unsubscribe();
        }
        if (this.timeout) {
            clearTimeout(this.timeout);
        }
    }
    setAutoDismiss(duration) {
        this.timeout = setTimeout(() => {
            this.dismiss();
        }, duration);
    }
    createOverlay() {
        this.overlayRef = this.overlay.create({
            positionStrategy: this.overlayConfig,
        });
    }
    applyConfig(override) {
        return { ...defaultToastConfig, ...override };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: GpuToastService, deps: [{ token: i1$1.Overlay }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: GpuToastService, providedIn: GpuToastModule }); }
};
GpuToastService = __decorate([
    UntilDestroy()
], GpuToastService);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: GpuToastService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: GpuToastModule,
                }]
        }], ctorParameters: () => [{ type: i1$1.Overlay }] });

/**
 * Generated bundle index. Do not edit.
 */

export { GpuToastComponent, GpuToastModule, GpuToastService, GpuToastType };
